import useToggle from "../../../../hooks/useToggle";
import { Button, Collapse } from "reactstrap";
import { ReactNode } from "react";
import classNames from "classnames";
import AppTooltip from "../../../../elements/AppTooltip";

interface CollapsibleGroupProps {
  icon?: string;
  title: string;
  description?: string | ReactNode;
  children?: ReactNode;
  size?: "sm" | "md" | "lg";
  startOpen?: boolean;
  collapsible?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  disabledMessage?: string;
  className?: string;
  containerClassName?: string;
}

// A Card with a title and description. The content of the card can be collapsed / hidden.
const CollapsibleGroup = ({
  icon,
  title,
  description,
  children,
  size = "md",
  startOpen = true,
  collapsible = true,
  onClick,
  disabled = false,
  disabledMessage,
  className,
  containerClassName,
}: CollapsibleGroupProps) => {
  const [open, toggleOpen] = useToggle(startOpen);

  const group = (
    <div
      className={`border border-superlight shadow-sm my-2 w-100 ${
        className ? className : ""
      }`}
      style={{ borderRadius: 5, overflow: "hidden" }}
      onClick={onClick ? (disabled ? undefined : onClick) : undefined}
    >
      <div
        className={classNames(
          "d-flex justify-content-between align-items-center",
          { "px-2 py-1": size === "sm" },
          { "p-3": size !== "sm" },
          { "border-bottom border-superlight -rounded-bottom": open },
          { "bg-white": !disabled },
          { "bg-superlight": disabled }
        )}
        onClick={collapsible ? toggleOpen : undefined}
        style={{
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <div className="d-flex align-items-center">
          {icon && (
            <i className={`${icon} mr-3 text-dark`} style={{ fontSize: 26 }} />
          )}
          <div>
            <h3 className="mb-1 p-0">{title}</h3>
            <div className="small">{description}</div>
          </div>
        </div>
        <Button color="secondary" outline className="btn-icon-only">
          <i
            className={`mdi mdi-chevron-${
              collapsible ? (open ? "down" : "right") : "right"
            }`}
          />
        </Button>
      </div>
      <Collapse isOpen={collapsible ? open : false}>
        <div className={`px-3 py-4 ${containerClassName || ""} `}>
          {children}
        </div>
      </Collapse>
    </div>
  );

  if (!disabled || !disabledMessage) return group;

  return (
    <AppTooltip useTooltip content={disabledMessage} placement="top">
      {group}
    </AppTooltip>
  );
};

export default CollapsibleGroup;
